import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";

import { dayjs } from "@thalamos/common";
import type {
  ManagedLocation,
  MhaDashboardDataParams,
} from "@thalamos/aspire-common";
import { MhaStatus } from "@thalamos/aspire-common";

import { userCanEditMhaStatus } from "../EditMhaStatus/userCanEditMhaStatus.js";
import { section136FilterEnabled } from "./defaultMhaStatusFilters.js";
import {
  MhaStatusDashboard,
  MhaStatusSearchBarControls,
} from "./MhaStatusDashboard.js";
import { apiHooks } from "~/api.js";
import { Banner, BannerList } from "~/components/design-system/index.js";
import type { MultiSelectFilterPickerOption } from "~/components/form/MultiSelectFilterPicker.js";
import { LoggedInUserContext, ManagedLocationsContext } from "~/Contexts.js";
import PaginationControls from "~/pages/Caseload/PaginationControls.js";
import { routeFns } from "~/routePaths.js";

const ROWS_PER_PAGE = 12;
const defaultStatuses: MhaStatus[] = [
  MhaStatus.Incomplete,
  MhaStatus.Expired,
  MhaStatus.Section17A,
  MhaStatus.Section2,
  MhaStatus.Section3,
  MhaStatus.Section4,
  MhaStatus.Section5_2,
  MhaStatus.Section5_4,
];

export function MhaStatusDashboardPage(_props: Record<string, never>) {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const userContext = useContext(LoggedInUserContext);
  const { managedLocations } = useContext(ManagedLocationsContext);
  const user = userContext?.user;

  const isNhsNumberConfigEnabled =
    user?.sessionOrganisationConfiguration?.nhsNumberEnabled ?? false;

  const isReportDownloadEnabled =
    user?.sessionOrganisationConfiguration?.mhaStatusReportEnabled ?? false;

  const section136Enabled = section136FilterEnabled(
    user?.sessionOrganisationConfiguration,
  );

  // Patient identifiers must never leak into the URL or any shared/persisted
  // state, so the search term is held in local state only.
  const [patientSearchFilter, setPatientSearchFilter] = useState("");

  const qs = useMemo(() => new URLSearchParams(search), [search]);
  const statusesFromUrl = qs.get("status");
  const pageFromUrl = qs.get("page") ?? "0";

  const trustIdsFromUrl = qs.get("trustIds");
  const hospitalIdsFromUrl = qs.get("hospitalIds");

  const params: Omit<MhaDashboardDataParams, "limit" | "offset"> = useMemo(
    () => ({
      // Section 136 is only a valid filter value when the org has it
      // enabled — drop it from URL-supplied statuses otherwise so it can't
      // be forced in via `?status=Section136` past the feature gate.
      statuses: (statusesFromUrl
        ? statusesFromUrl.split(",").map((status) => status as MhaStatus)
        : section136Enabled
          ? [...defaultStatuses, MhaStatus.Section136]
          : defaultStatuses
      ).filter(
        (status) => section136Enabled || status !== MhaStatus.Section136,
      ),
      trustIds: trustIdsFromUrl ? trustIdsFromUrl.split(",") : undefined,
      hospitalIds: hospitalIdsFromUrl
        ? hospitalIdsFromUrl.split(",")
        : undefined,
      expiryLessThan: qs.get("expiryLessThan") ?? "",
    }),
    [qs, section136Enabled],
  );

  const showMhaDashboardLink = userCanEditMhaStatus(user);

  apiHooks.reports.recordMhaDashboardView(
    showMhaDashboardLink
      ? (user?.sessionContext?.organisationId ?? null)
      : null,
  );

  const [{ data: trustsData }] = apiHooks.patients.getMhaDashboardTrusts();
  const availableTrusts: MultiSelectFilterPickerOption[] = useMemo(
    () =>
      (trustsData?.availableTrusts ?? []).map((t) => ({
        id: t.trustId,
        name: t.name,
      })),
    [trustsData],
  );

  // Default trust selection to current trust on first load only
  const hasInitialisedTrusts = useRef(!!trustIdsFromUrl);
  useEffect(() => {
    if (hasInitialisedTrusts.current) return;
    if (
      trustsData?.currentTrustId &&
      !trustIdsFromUrl &&
      availableTrusts.length > 0
    ) {
      const currentTrust = availableTrusts.find(
        (t) => t.id === trustsData.currentTrustId,
      );
      if (currentTrust) {
        hasInitialisedTrusts.current = true;
        navigate(
          routeFns.mhaStatusDashboardPage(0, {
            ...params,
            trustIds: [currentTrust.id],
          }),
          { replace: true },
        );
      }
    }
  }, [
    trustsData?.currentTrustId,
    trustIdsFromUrl,
    availableTrusts,
    navigate,
    params,
  ]);

  // Derive hospital options filtered by selected trusts
  const hospitalOptions: MultiSelectFilterPickerOption[] = useMemo(() => {
    const availableTrustIds = availableTrusts.map((t) => t.id);

    const hospitals = managedLocations
      .filter((loc: ManagedLocation) => loc.type === "hospital")
      .filter(
        (h: ManagedLocation) =>
          h.trustId && availableTrustIds.includes(h.trustId),
      )
      .sort((a: ManagedLocation, b: ManagedLocation) =>
        a.name.localeCompare(b.name),
      );

    if (params.trustIds && params.trustIds.length > 0) {
      return hospitals
        .filter(
          (h: ManagedLocation) =>
            h.trustId && params.trustIds?.includes(h.trustId),
        )
        .map((h: ManagedLocation) => ({ id: h.id, name: h.name }));
    }

    return hospitals.map((h: ManagedLocation) => ({ id: h.id, name: h.name }));
  }, [managedLocations, params.trustIds, availableTrusts]);

  // Prune hospital selections when trust filter changes
  useEffect(() => {
    if (params.hospitalIds && params.trustIds && params.trustIds.length > 0) {
      const validHospitalIds = hospitalOptions.map((h) => h.id);
      const pruned = params.hospitalIds.filter((id) =>
        validHospitalIds.includes(id),
      );
      if (pruned.length !== params.hospitalIds.length) {
        navigate(
          routeFns.mhaStatusDashboardPage(parseInt(pageFromUrl), {
            ...params,
            hospitalIds: pruned.length > 0 ? pruned : undefined,
          }),
          { replace: true },
        );
      }
    }
  }, [hospitalOptions, params, pageFromUrl, navigate]);

  const [{ data, loading, error }, refetchDashboard] =
    apiHooks.patients.getMhaDashboardData({
      ...params,
      patientSearchFilter: patientSearchFilter,
      limit: ROWS_PER_PAGE,
      offset: parseInt(pageFromUrl) * ROWS_PER_PAGE,
    });

  const updateParams = useCallback(
    (params: MhaDashboardDataParams) => {
      // Reset page to 0 on any change of params
      navigate(routeFns.mhaStatusDashboardPage(0, params));
    },
    [navigate, pathname],
  );

  function formatSubTitle(subTitle: string, date: string) {
    return (
      <>
        {subTitle} <span style={{ fontWeight: "bold" }}>{date}</span>
      </>
    );
  }

  return !showMhaDashboardLink ? (
    <Banner
      bannerType={BannerList.ERROR}
      title={"Incorrect permissions to view MHA status dashboard"}
    />
  ) : (
    <>
      <MhaStatusSearchBarControls
        subTitle={formatSubTitle(
          "MHA Detention Statuses",
          dayjs().format("DD MMM YYYY"),
        )}
        title={userContext?.user?.sessionContext?.dataControllerName ?? ""}
        params={params}
        patientSearchFilter={patientSearchFilter}
        setPatientSearchFilter={setPatientSearchFilter}
        updateParams={updateParams}
        showReportDownload={isReportDownloadEnabled}
        section136Enabled={section136Enabled}
        availableTrusts={availableTrusts}
        hospitalOptions={hospitalOptions}
      />
      {!loading && !error && (data?.rows.length ?? 0) > 0 && (
        <PaginationControls
          page={parseInt(pageFromUrl ?? "1")}
          setPage={(page) => {
            navigate(routeFns.mhaStatusDashboardPage(page, params));
          }}
          numPages={Math.ceil((data?.total ?? 0) / ROWS_PER_PAGE)}
        />
      )}
      <MhaStatusDashboard
        data={data}
        error={error?.message}
        isLoading={loading}
        showNhsNumber={isNhsNumberConfigEnabled}
        onS132Changed={() => {
          void refetchDashboard();
        }}
      />
    </>
  );
}
